import { FileDragDrop } from 'components/FileDragDrop'
import { BaseTextInput } from 'components/FormUtils/TextInput/text-input'
import { BaseExportModal } from 'components/Modals/ExportModal/ExportModal'
import { Modal } from 'components/UI/Modal/Modal'
import { Select } from 'components/UI/Select/Select'
import { Switch } from 'components/UI/Switch/Switch'
import { FBPage } from '../../components/FbUI/Page/FBPage'
import { ImporterMatchModal } from './ImporterMatchModal'
import { useImporterViewModel } from './ImporterViewModel'
import { ImportWizard } from './Wizard/ImportWizard'
import { ManualSearchModal } from './ManualSearchModal'
import { ValidateModal } from './validate-modal'
import { FBPageTableContainer } from 'components/FbUI/Page/styles'

const TABLE_CONTAINER_HEIGHT = 'calc(var(--base-page-table-height))'

export default function Importer(props: { importNumber: number | null }) {
  const viewModel = useImporterViewModel(props.importNumber)

  if (viewModel.importPreviewStatus?.status === 'running') {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center">
        <div className="text-sm">
          {viewModel.importPreviewStatus.process_running} in progress, wait or
          come back later...
        </div>
      </div>
    )
  }

  if (!props.importNumber) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col w-full h-full justify-center items-center">
        <div className="p-4 w-full">
          {!props.importNumber && (
            <FileDragDrop
              file={viewModel.rawFile}
              onFileChange={viewModel.handleFileUpload}
            />
          )}
        </div>
        <ImporterMatchModal
          data={viewModel.importData}
          open={viewModel.showHeaderMappingModal}
          onClose={() => viewModel.setShowHeaderMappingModal(false)}
          onSave={async (data) => {
            viewModel.setShowHeaderMappingModal(false)
            await viewModel.handleSave(data)
          }}
        />
      </div>
    )
  }

  return (
    <FBPage>
      <FBPageTableContainer style={{ height: TABLE_CONTAINER_HEIGHT }}>
        <ImportWizard viewModel={viewModel} />
      </FBPageTableContainer>

      <ManualSearchModal
        show={viewModel.selectedRowId !== null}
        onClose={() => {
          viewModel.setSelectedRowId(null)
        }}
        onSelect={viewModel.handleManualSearchSelect}
      />

      <ValidateModal
        open={!!viewModel.validateModalData}
        onClose={() => viewModel.setValidateModalData(null)}
        onValidate={(data) => {
          viewModel.handleValidation(data.changes, data.columnName)
        }}
        data={viewModel.validateModalData}
      />

      {/* Select Deal Product Modal */}
      <Modal
        open={viewModel.showSelectDealProductModal}
        disableCloseIcon
        title="Select Deal Product"
        description="The deal import requires a product to be selected. Since the product column is not present in the uploaded CSV, you must select the product from the dropdown below."
        size="lg"
        blockAccept={viewModel.selectedDealProduct == null}
        acceptButtonText="Select Deal Product"
        onAccept={() => {
          if (viewModel.selectedDealProduct) {
            viewModel.handleSelectDealProduct(viewModel.selectedDealProduct)
          }
        }}
        onCancel={() => {
          viewModel.setShowSelectDealProductModal(false)
          viewModel.setSelectedDealProduct(null)
        }}
      >
        <Select
          className="w-full"
          items={viewModel.products?.map((product) => ({
            label: product.name,
            value: product.name,
          }))}
          selected={viewModel.selectedDealProduct}
          onSelect={(value) => viewModel.setSelectedDealProduct(value)}
        />
      </Modal>

      <Modal
        open={viewModel.showConfigureExportModal}
        title="Configure Export"
        description="Configure the export options for the importer."
        size="lg"
        onCancel={() => viewModel.setShowConfigureExportModal(false)}
        acceptButtonText="Done"
        onAccept={() => viewModel.handleConfigureExport()}
      >
        <div className="flex flex-col gap-6 p-2">
          {/* Suggestions Toggle */}
          <div className="flex flex-row items-center gap-3">
            <Switch
              checked={viewModel.exportConfig.allowSuggestions}
              onChange={(e) =>
                viewModel.setExportConfig({
                  ...viewModel.exportConfig,
                  allowSuggestions: e.target.checked,
                })
              }
            />
            <div className="font-medium text-gray-800">
              Include Suggested Data
            </div>
          </div>

          {/* Suggestion Amount */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="suggestionAmount"
              className={`text-sm font-medium text-gray-700 ${
                !viewModel.exportConfig.allowSuggestions ? 'opacity-50' : ''
              }`}
            >
              Suggestion Amount
            </label>
            <BaseTextInput
              value={viewModel.exportConfig.suggestionAmount}
              name="suggestionAmount"
              type="number"
              min={1}
              max={10}
              disabled={!viewModel.exportConfig.allowSuggestions}
              onChange={(value) =>
                viewModel.setExportConfig({
                  ...viewModel.exportConfig,
                  suggestionAmount: Number(value),
                })
              }
              className="w-32"
            />
            <span className="text-xs text-gray-500">
              Number of suggestions to include per row (1-10)
            </span>
          </div>
        </div>
      </Modal>

      <BaseExportModal
        title="Select Columns to Export"
        subTitle="Export up to 10,000 rows at a time, with any of the following columns. Any filters already applied will remain active."
        submitText="Export"
        columns={viewModel.exportColumns}
        onSubmit={viewModel.handleExport}
        show={viewModel.showExportModal}
        handleClose={() => viewModel.setShowExportModal(false)}
        disableExportTypes
      />
    </FBPage>
  )
}
